<template>
  <footer class="footer-wrapper mt-auto" @mousemove="handleGlobalMouseMove" @mouseleave="resetTilt" ref="footerRef">
    <!-- QUANTUM PARTICLES CANVAS -->
    <canvas ref="particlesCanvas" class="particles-canvas"></canvas>

    <!-- Dynamic Background Effects -->
    <div class="footer-ambient-bg">
      <div class="ambient-glow" :style="glowStyle"></div>
    </div>

    <div class="tilt-perspective-wrapper">
      <div class="footer-dock glass-panel" :style="tiltStyle">
        
        <!-- Top Dynamic Border -->
        <div class="gradient-border-top"></div>

        <div class="container-fluid px-4 content-layer">
          <div class="row align-items-center g-4">
            
            <!-- Brand & Copyright -->
            <div class="col-md-4">
              <div class="d-flex align-items-center justify-content-center justify-content-md-start gap-3 brand-section">
                <div class="brand-logo-container magnetic-trigger" ref="brandLogo">
                  <div class="brand-icon">
                    <i class="bi bi-building-fill"></i>
                  </div>
                  <div class="brand-glow"></div>
                </div>
                
                <div class="d-flex flex-column">
                  <!-- HOLOGRAPHIC TEXT -->
                  <span class="footer-brand-text holographic-text">PORTEO</span>
                  <div class="d-flex align-items-center gap-2 small-text">
                    <span class="copyright opacity-75">&copy; 2026</span>
                    <span class="divider">|</span>
                    <a href="https://www.linkedin.com/in/a-cherifi" target="_blank" class="author-link">
                      Cherifi.A
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <!-- Central Dashboard / Status -->
            <div class="col-md-4">
              <div class="system-dashboard glass-inset">
                <div class="d-flex align-items-center justify-content-center gap-4">
                  
                  <!-- Live Clock -->
                  <div class="dashboard-item">
                    <i class="bi bi-clock text-primary mb-1 d-block text-center" style="font-size: 0.8rem;"></i>
                    <span class="digital-clock">{{ currentTime }}</span>
                  </div>

                  <div class="vertical-divider"></div>

                  <!-- System Status -->
                  <div class="dashboard-item status-item" title="Systèmes opérationnels">
                    <div class="status-indicator">
                      <span class="pulse-ring"></span>
                      <span class="pulse-dot"></span>
                    </div>
                    <span class="status-label">ONLINE</span>
                  </div>

                  <div class="vertical-divider"></div>
                  
                   <!-- Report Bug -->
                   <router-link to="/feedback" class="dashboard-item action-item" title="Signaler un problème">
                     <i class="bi bi-bug text-warning mb-1 d-block text-center" style="font-size: 0.8rem;"></i>
                     <span class="action-text">REPORT</span>
                   </router-link>

                </div>
              </div>
            </div>

            <!-- Quick Actions -->
            <div class="col-md-4 text-center text-md-end">
               <div class="d-flex align-items-center justify-content-center justify-content-md-end gap-3">
                  <button @click="scrollToTop" class="magnetic-btn glass-btn" ref="topBtn">
                    <span class="btn-content">
                      <span class="btn-text me-2">TOP</span>
                      <i class="bi bi-arrow-up-short"></i>
                    </span>
                  </button>
               </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
export default {
  name: 'Footer',
  data() {
    return {
      currentTime: '',
      mouseX: 0,
      mouseY: 0,
      timer: null,
      // 3D Tilt Data
      rotateX: 0,
      rotateY: 0,
      // Particles Data
      animationFrame: null,
      particles: []
    }
  },
  computed: {
    glowStyle() {
      // Glow follows mouse on the 2D plane
      const rect = this.$refs.footerRef ? this.$refs.footerRef.getBoundingClientRect() : { left:0, top:0 };
      const localX = this.mouseX - rect.left;
      const localY = this.mouseY - rect.top;
      return {
        background: `radial-gradient(600px circle at ${localX}px ${localY}px, rgba(99, 102, 241, 0.15), transparent 40%)`
      }
    },
    tiltStyle() {
      return {
        transform: `rotateX(${this.rotateX}deg) rotateY(${this.rotateY}deg)`
      }
    }
  },
  mounted() {
    this.updateTime();
    this.timer = setInterval(this.updateTime, 1000);
    this.initParticles();
    window.addEventListener('resize', this.resizeCanvas);
  },
  beforeUnmount() {
    clearInterval(this.timer);
    cancelAnimationFrame(this.animationFrame);
    window.removeEventListener('resize', this.resizeCanvas);
  },
  methods: {
    updateTime() {
      const now = new Date();
      this.currentTime = now.toLocaleTimeString('fr-FR', { 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit' 
      });
    },
    handleGlobalMouseMove(e) {
      this.mouseX = e.clientX;
      this.mouseY = e.clientY;

      this.calcTilt(e);
    },
    calcTilt(e) {
      if (!this.$refs.footerRef) return;
      
      const rect = this.$refs.footerRef.getBoundingClientRect();
      const x = e.clientX - rect.left; // x position within the element.
      const y = e.clientY - rect.top;  // y position within the element.
      
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      
      // Calculate rotation based on cursor position relative to center
      // Max rotation: 5 degrees
      const maxRotation = 5;
      
      // Invert Y axis for correct tilt feel (mouse up -> tilt back)
      this.rotateX = ((y - centerY) / centerY) * -maxRotation; 
      this.rotateY = ((x - centerX) / centerX) * maxRotation;
    },
    resetTilt() {
      this.rotateX = 0;
      this.rotateY = 0;
    },
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    },
    
    // --- PARTICLE SYSTEM ---
    initParticles() {
      const canvas = this.$refs.particlesCanvas;
      const ctx = canvas.getContext('2d');
      
      this.resizeCanvas();
      
      // Create particles
      const particleCount = 50;
      for (let i = 0; i < particleCount; i++) {
        this.particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          size: Math.random() * 2 + 0.5
        });
      }

      this.animateParticles();
    },
    resizeCanvas() {
      const canvas = this.$refs.particlesCanvas;
      if (canvas && this.$refs.footerRef) {
        canvas.width = this.$refs.footerRef.offsetWidth;
        canvas.height = this.$refs.footerRef.offsetHeight;
      }
    },
    animateParticles() {
      const canvas = this.$refs.particlesCanvas;
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      ctx.fillStyle = 'rgba(129, 140, 248, 0.5)';
      ctx.strokeStyle = 'rgba(129, 140, 248, 0.1)';
      
      // Update and draw particles
      this.particles.forEach((p, index) => {
        p.x += p.vx;
        p.y += p.vy;
        
        // Bounce off walls
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        
        // Draw particle
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
        
        // Connect near particles
        for (let j = index + 1; j < this.particles.length; j++) {
            const p2 = this.particles[j];
            const dx = p.x - p2.x;
            const dy = p.y - p2.y;
            const dist = Math.sqrt(dx*dx + dy*dy);
            
            if (dist < 100) {
                ctx.beginPath();
                ctx.moveTo(p.x, p.y);
                ctx.lineTo(p2.x, p2.y);
                ctx.stroke();
            }
        }

        // Connect to mouse if close
        const rect = this.$refs.footerRef.getBoundingClientRect();
        const localMouseX = this.mouseX - rect.left;
        const localMouseY = this.mouseY - rect.top;
        
        const dx = p.x - localMouseX;
        const dy = p.y - localMouseY;
        const dist = Math.sqrt(dx*dx + dy*dy);
        
        if (dist < 150) {
             ctx.beginPath();
             ctx.strokeStyle = `rgba(129, 140, 248, ${1 - dist/150})`;
             ctx.moveTo(p.x, p.y);
             ctx.lineTo(localMouseX, localMouseY);
             ctx.stroke();
             // Reset stroke style
             ctx.strokeStyle = 'rgba(129, 140, 248, 0.1)';
        }
      });
      
      this.animationFrame = requestAnimationFrame(this.animateParticles);
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700&display=swap');

.footer-wrapper {
  position: relative;
  z-index: 1000;
  padding: 3rem 1rem;
  background: #0f172a;
  overflow: hidden;
  margin-top: auto;
  perspective: 2000px;
  pointer-events: auto; /* Required for mousemove listener */
}

/* Background elements MUST ignore clicks */
.particles-canvas, .footer-ambient-bg, .ambient-glow, .grid-overlay {
    pointer-events: none !important;
}

.particles-canvas {
    position: absolute;
    inset: 0;
    z-index: 0;
    opacity: 0.6;
}

.footer-ambient-bg {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: radial-gradient(circle at 50% 120%, rgba(56, 189, 248, 0.15), transparent 70%);
}

.ambient-glow {
  position: absolute;
  inset: 0;
  transition: opacity 0.3s;
}

.grid-overlay {
  position: absolute;
  inset: 0;
  background-image: 
    linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 30px 30px;
  mask-image: radial-gradient(circle at center, black, transparent 80%);
  opacity: 0.3;
}

/* 3D TILT CONTAINER */
.tilt-perspective-wrapper {
    position: relative;
    z-index: 10;
    transform-style: preserve-3d;
    will-change: transform;
    pointer-events: none; /* Let clicks pass through to dock */
}

/* Levitating Dock */
.footer-dock {
  position: relative;
  border-radius: 24px;
  padding: 1.5rem;
  pointer-events: auto; /* Captures clicks! */
  
  /* 3D Properties */
  transform-style: preserve-3d;
  transition: transform 0.1s ease-out;
}

.glass-panel {
  background: rgba(30, 41, 59, 0.4);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 
    0 20px 50px -12px rgba(0, 0, 0, 0.5),
    inset 0 1px 1px rgba(255, 255, 255, 0.1);
}

.content-layer {
    position: relative;
    z-index: 50;
    transform: translateZ(20px); /* Lower depth to avoid click mapping issues */
    pointer-events: auto !important;
}

.gradient-border-top {
  position: absolute;
  top: 0;
  left: 10%;
  right: 10%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(99, 102, 241, 0.8), transparent);
  opacity: 0.5;
}

/* Brand Section */
.brand-logo-container {
  position: relative;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.02));
  border: 1px solid rgba(255,255,255,0.1);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform-style: preserve-3d;
}

.brand-icon {
  font-size: 1.5rem;
  color: #818cf8;
  z-index: 2;
  filter: drop-shadow(0 0 8px rgba(99, 102, 241, 0.5));
  transform: translateZ(20px);
}

.brand-logo-container:hover {
  transform: translateY(-5px) scale(1.05) translateZ(10px);
  border-color: rgba(99, 102, 241, 0.5);
  box-shadow: 0 10px 20px rgba(0,0,0,0.2);
}

/* Holographic Text */
.holographic-text {
  font-family: 'Outfit', sans-serif;
  font-weight: 800;
  font-size: 1.25rem;
  letter-spacing: -0.02em;
  
  /* Hologram Gradient */
  background: linear-gradient(
    135deg, 
    #fff 0%, 
    #94a3b8 25%, 
    #818cf8 50%, 
    #c084fc 75%, 
    #fff 100%
  );
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: hologram-shine 5s linear infinite;
}

@keyframes hologram-shine {
    to {
        background-position: 200% center;
    }
}

.small-text {
  font-size: 0.8rem;
  color: #94a3b8;
}

.author-link {
  color: #818cf8;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s;
  pointer-events: auto;
  position: relative;
  z-index: 120;
}

.author-link:hover {
  color: #fff;
  text-shadow: 0 0 10px rgba(129, 140, 248, 0.8);
}

/* Dashboard & Clock */
.system-dashboard {
  background: rgba(15, 23, 42, 0.6);
  border-radius: 16px;
  padding: 0.75rem 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.03);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2);
  display: inline-block;
  transform: translateZ(15px);
}

.glass-inset {
  position: relative;
  overflow: hidden;
}

.digital-clock {
  font-family: 'JetBrains Mono', monospace;
  font-weight: 700;
  color: #e2e8f0;
  font-size: 0.9rem;
  letter-spacing: 0.5px;
}

.status-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.status-indicator {
  position: relative;
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2px;
}

.pulse-dot {
  width: 6px;
  height: 6px;
  background: #10b981;
  border-radius: 50%;
  box-shadow: 0 0 5px #10b981;
}

.pulse-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1px solid #10b981;
  animation: ripple 2s infinite;
  opacity: 0;
}

.status-label {
  font-size: 0.6rem;
  font-weight: 800;
  color: #10b981;
  letter-spacing: 0.05em;
}

.vertical-divider {
  width: 1px;
  height: 24px;
  background: rgba(255, 255, 255, 0.1);
}

.action-item {
  text-decoration: none;
  transition: transform 0.2s;
  pointer-events: auto;
  position: relative;
  z-index: 120;
}

.action-item:hover {
  transform: scale(1.1);
}

.action-text {
  font-size: 0.6rem;
  font-weight: 700;
  color: #f59e0b;
  letter-spacing: 0.05em;
}

/* Buttons */
.glass-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
  padding: 0.6rem 1.2rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  transform: translateZ(40px);
  pointer-events: auto;
  z-index: 110;
}

.glass-btn:hover {
  background: rgba(99, 102, 241, 0.2);
  border-color: rgba(99, 102, 241, 0.4);
  box-shadow: 0 0 20px rgba(99, 102, 241, 0.3);
  transform: translateY(-2px) translateZ(25px);
}

.glass-btn::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 60%);
  opacity: 0;
  transform: scale(0.5);
  transition: opacity 0.3s, transform 0.3s;
}

.glass-btn:hover::after {
  opacity: 1;
  transform: scale(1);
}

.btn-text {
  font-size: 0.8rem;
  letter-spacing: 0.05em;
}

/* Animations */
@keyframes ripple {
  0% { transform: scale(0.8); opacity: 0.8; }
  100% { transform: scale(2.5); opacity: 0; }
}

@media (max-width: 768px) {
  .footer-dock {
    padding: 1rem;
    border-radius: 20px 20px 0 0;
    transform: none !important; /* Disable tilt on mobile */
  }
  
  .system-dashboard {
    width: 100%;
    margin: 1rem 0;
  }
}
</style>
